import React, { useEffect, useRef, useState } from 'react';
import { ProjectType } from '../../projectsData';
import './projectDetails.css';

const formatReadme = (content: string): string => {
  if (!content) return '';
  return content
    .replace(/^# (.*$)/gm, '<h1>$1</h1>')
    .replace(/^## (.*$)/gm, '<h2>$1</h2>')
    .replace(/^### (.*$)/gm, '<h3>$1</h3>')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^\* (.*$)/gm, '<ul><li>$1</li></ul>')
    .replace(/<\/ul>\s*<ul>/g, '')
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\n/g, '<br />');
};

const SECTIONS = [
  { id: 'description', label: 'Description' },
  { id: 'features',    label: 'Features'    },
  { id: 'tech',        label: 'Stack'       },
  { id: 'links',       label: 'Links'       },
];

interface ProjectDetailsProps {
  project: ProjectType;
  onClose: () => void;
}

const ProjectDetails: React.FC<ProjectDetailsProps> = ({ project, onClose }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef      = useRef<HTMLDivElement>(null);
  const [progress, setProgress]         = useState(0);
  const [activeSection, setActiveSection] = useState(0);

  // ESC key + body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Scroll → reading progress + hero parallax
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = el;
      setProgress(scrollHeight > clientHeight ? (scrollTop / (scrollHeight - clientHeight)) * 100 : 0);
      if (heroRef.current) {
        heroRef.current.style.transform = `translateY(${scrollTop * 0.45}px)`;
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Section progress dots — IntersectionObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const sectionEls = SECTIONS.map(s =>
      el.querySelector(`[data-section="${s.id}"]`)
    ).filter(Boolean) as Element[];

    const obs = new IntersectionObserver(
      entries => entries.forEach(entry => {
        if (entry.isIntersecting) {
          const idx = sectionEls.indexOf(entry.target);
          if (idx !== -1) setActiveSection(idx);
        }
      }),
      { threshold: 0.35, root: el }
    );
    sectionEls.forEach(s => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    containerRef.current
      ?.querySelector(`[data-section="${id}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="project-details__container" ref={containerRef}>
      {/* ── Sticky header with glassmorphism ── */}
      <div className="project-details__header">
        <div className="project-details__header-content">
          <h1>
            <span className="project-details__header-icon">
              <i className="uil uil-code-branch"></i>
            </span>
            {project.title}
          </h1>
          <button
            className="project-details__close"
            onClick={onClose}
            aria-label="Close project details"
          >
            <i className="uil uil-times"></i>
          </button>
        </div>
        {/* Reading progress bar lives at the bottom of the header */}
        <div className="pdp__progress" style={{ width: `${progress}%` }} />
      </div>

      {/* ── Section navigation dots ── */}
      <nav className="pdp__sections-nav" aria-label="Page sections">
        {SECTIONS.map((s, i) => (
          <button
            key={s.id}
            className={`pdp__section-dot${i === activeSection ? ' pdp__section-dot--active' : ''}`}
            onClick={() => scrollTo(s.id)}
            aria-label={s.label}
          >
            <span className="pdp__section-dot-label">{s.label}</span>
          </button>
        ))}
      </nav>

      <div className="project-details__body">
        {/* ── Hero image with parallax ── */}
        <div className="project-details__main-image">
          <div className="pdp__parallax-wrap" ref={heroRef}>
            <img src={project.image} alt={project.title} />
          </div>
          <div className="project-details__category-badge">{project.category}</div>
        </div>

        <div className="project-details__content">
          <div className="project-details__section" data-section="description">
            <h2><i className="uil uil-document-info"></i> Description</h2>
            <p>{project.description}</p>
          </div>

          <div className="project-details__section" data-section="features">
            <h2><i className="uil uil-star"></i> Key Features</h2>
            <ul className="project-details__features">
              {project.features.map((feature, index) => (
                <li key={index}>
                  <i className="uil uil-check-circle"></i>
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="project-details__section" data-section="tech">
            <h2><i className="uil uil-window-grid"></i> Technologies</h2>
            <div className="project-details__tech">
              {project.tech.map((item, index) => (
                <span key={index} className="project-details__tech-item">{item}</span>
              ))}
            </div>
          </div>

          {project.readmeContent && (
            <div className="project-details__section">
              <h2><i className="uil uil-file-alt"></i> Project Documentation</h2>
              <div className="project-details__readme">
                <div
                  className="markdown-content"
                  dangerouslySetInnerHTML={{ __html: formatReadme(project.readmeContent) }}
                />
              </div>
            </div>
          )}

          <div className="project-details__section" data-section="links">
            <h2><i className="uil uil-link"></i> Links</h2>
            <div className="project-details__links">
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="project-details__link"
              >
                <i className="uil uil-github"></i> View on GitHub
              </a>
              {project.demo && (
                <a
                  href={project.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-details__link project-details__link--demo"
                >
                  <i className="uil uil-eye"></i> Live Demo
                </a>
              )}
            </div>
          </div>
        </div>

        <div className="project-details__footer">
          <button onClick={onClose} className="project-details__back-button">
            <i className="uil uil-arrow-left"></i> Back to Projects
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetails;
